/**
 * AcenteOpsPricing — operasyon-facing fiyat onay kuyruğu.
 *
 * Amaç: Acentelerin kendi rezervasyonlarında girdikleri "Anlaşılan Fiyat"
 * bilgisini operasyon ekibinin hızlıca inceleyip **tek tıkla** onaylaması ya
 * da **yeni fiyat ile üzerine yazması** için sade bir tablo.
 *
 * Kapsam (admin + operasyon):
 *   - `GET /reservations?ops_pending=true` — sadece ops_review_status=pending
 *     olan satırlar.
 *   - Kolonlar (kasten kısa): Şirket · Giriş tarihi · Oda / Yemek / Toplam
 *     (acentenin girdiği fiyatlar) · Aksiyon
 *   - Aksiyonlar:
 *       * ✅ Onayla   → POST /reservations/{id}/ops-review {approve:true}
 *         (agreed → locked kopyalanır, status = onaylandi)
 *       * ✏️ Fiyat Değiştir → küçük modal, yeni Oda + Yemek fiyatı girilir
 *         → POST /ops-review {approve:true, room_price, meal_price, ops_note}
 *
 * Diğer sayfalar (Rezervasyonlar, Detay) mevcut haliyle çalışmaya devam
 * eder; bu ekran onların yerine değil, YANINA konumlandırıldı — Fiyat
 * Onayları sidebar'da ayrı bir link olarak eklenir.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import panelApi from "@/lib/panelApi";
import { formatTr } from "@/lib/date";
import { totalMealCount, totalRoomsFromDist, computeReservationTotal } from "@/lib/pricing";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2, Pencil, Wallet, ArrowLeft, RefreshCw, Building2, Calendar, XCircle, Check, X,
  Sunrise, Clock, Sparkles, ThumbsUp, AlertCircle,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Iter 30: compact KPI card for the ops morning-summary strip.
// tone: orange | rose | amber | emerald | slate  — subtle bg + icon color.
const KPI_TONES = {
  orange:  { bg: "bg-brand-orange/5",   border: "border-brand-orange/30",  icon: "bg-brand-orange/15 text-brand-orange-dark",  num: "text-brand-orange-dark" },
  rose:    { bg: "bg-rose-50",          border: "border-rose-200",         icon: "bg-rose-100 text-rose-700",                  num: "text-rose-700" },
  amber:   { bg: "bg-amber-50",         border: "border-amber-200",        icon: "bg-amber-100 text-amber-700",                num: "text-amber-700" },
  emerald: { bg: "bg-emerald-50",       border: "border-emerald-200",      icon: "bg-emerald-100 text-emerald-700",            num: "text-emerald-700" },
  slate:   { bg: "bg-slate-50",         border: "border-slate-200",        icon: "bg-slate-100 text-slate-500",                num: "text-slate-500" },
};
function KPICard({ icon, label, value, tone = "slate", hint, testid }) {
  const t = KPI_TONES[tone] || KPI_TONES.slate;
  return (
    <div className={`rounded-xl border ${t.border} ${t.bg} p-3 flex items-center gap-3`} data-testid={testid}>
      <div className={`w-9 h-9 rounded-lg ${t.icon} flex items-center justify-center flex-shrink-0`}>{icon}</div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold truncate">{label}</div>
        <div className={`font-serif text-2xl leading-none mt-0.5 ${t.num}`} data-testid={testid + "-value"}>{value}</div>
        {hint && <div className="text-[9px] text-slate-400 mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}

export default function AcenteOpsPricing() {
  const [rows, setRows] = useState([]);
  const [agencies, setAgencies] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  // Override modal state — one row at a time to keep the mental model simple.
  const [overrideTarget, setOverrideTarget] = useState(null); // { row, room, meal, note, busy }
  // Rejection modal target — one row at a time, reason is mandatory.
  const [rejectTarget, setRejectTarget] = useState(null); // { row, reason, busy }
  // Iter 29: toplu seçim. `selectedIds` is a Set of reservation ids; the
  // header checkbox toggles all filtered rows on/off. Bulk approve applies
  // the acente's agreed prices; bulk reject prompts for a single shared reason.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  // Iter 30: sabah özeti KPI'ları — ops paneli açar açmaz bir bakışta
  // günün önceliklerini görsün.
  const [summary, setSummary] = useState(null); // { pending, checkin_today, checkin_tomorrow, approved_week, rejected_active }
  const loadSummary = useCallback(async () => {
    try {
      const r = await panelApi.get("/reservations/ops-summary");
      setSummary(r.data);
    } catch {
      // Silent fail — KPI strip is informational, don't spam toast if API glitches.
    }
  }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const load = useCallback(async () => {
    try {
      // Two calls in parallel — the queue itself + agencies lookup for names.
      const [rezR, agR] = await Promise.all([
        panelApi.get("/reservations", { params: { ops_pending: true } }),
        panelApi.get("/agencies").catch(() => ({ data: [] })),
      ]);
      setRows(rezR.data || []);
      setAgencies(Object.fromEntries((agR.data || []).map((a) => [a.id, a.name])));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Fiyat onay listesi alınamadı");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Auto-refresh every 30s so operasyon sees new pending rows without
    // hitting the refresh button (small enough queue for polling).
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [load]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = !needle ? rows : rows.filter((r) =>
      (agencies[r.agency_id] || "").toLowerCase().includes(needle) ||
      r.reservation_number?.toLowerCase().includes(needle) ||
      r.group_name?.toLowerCase().includes(needle) ||
      r.hotel_name?.toLowerCase().includes(needle) ||
      r.customer_phone?.toLowerCase().includes(needle)
    );

    // Iter 28: risk skorlaması. Ops ekibi her rezervasyona bakmak zorunda —
    // "hangisiyle önce ilgileneyim?" sorusunun cevabı otomatik gelmeli.
    // 3 faktör:
    //   1. Yüksek tutar         → toplam agreed price
    //   2. Çıkışa yakın         → checkin tarihine az gün kaldı
    //   3. Yeni acente          → ilk 3 rezervasyondan biri
    // Her faktör 0-40 arası puan, toplam 0-100. `Yüksek/Orta/Düşük` etiketi.
    const now = Date.now();
    const agencyCount = {};
    for (const r of rows) {
      if (!r.agency_id) continue;
      agencyCount[r.agency_id] = (agencyCount[r.agency_id] || 0) + 1;
    }

    // Toplam agreed price'ların referans max'ı — normalize etmek için.
    const totals = list.map((r) => (Number(r.agreed_room_price || 0) + Number(r.agreed_meal_price || 0)));
    const maxTotal = Math.max(1, ...totals);

    const scored = list.map((r) => {
      const total = Number(r.agreed_room_price || 0) + Number(r.agreed_meal_price || 0);
      const totalScore = Math.min(40, Math.round((total / maxTotal) * 40));

      // Çıkışa yakın — 30 günden az kalırsa artan skala; 3 günden azsa max.
      let daysUntil = 999;
      if (r.checkin) {
        const ci = new Date(r.checkin + "T00:00:00").getTime();
        daysUntil = Math.round((ci - now) / 86400000);
      }
      let timeScore = 0;
      if (daysUntil <= 3) timeScore = 40;
      else if (daysUntil <= 7) timeScore = 30;
      else if (daysUntil <= 14) timeScore = 20;
      else if (daysUntil <= 30) timeScore = 10;

      // Yeni acente — acentenin (ekrandaki) toplam kayıt sayısı ≤ 3.
      const cnt = r.agency_id ? (agencyCount[r.agency_id] || 0) : 0;
      let newAgencyScore = 0;
      if (cnt <= 1) newAgencyScore = 20;
      else if (cnt <= 3) newAgencyScore = 10;

      const score = totalScore + timeScore + newAgencyScore;
      const level = score >= 70 ? "high" : score >= 40 ? "med" : "low";
      // İlk kayıt gelen acente uyarısı — ops elle bir kez daha bakmak isteyebilir.
      const isNewAgency = cnt <= 1;
      return { ...r, _risk: { score, level, daysUntil, isNewAgency, total } };
    });

    // Yüksek risk üste — aynı skorda çıkışa yakın olan öne.
    scored.sort((a, b) => {
      if (b._risk.score !== a._risk.score) return b._risk.score - a._risk.score;
      return a._risk.daysUntil - b._risk.daysUntil;
    });
    return scored;
  }, [rows, agencies, q]);

  const approveAsIs = async (row) => {
    try {
      await panelApi.post(`/reservations/${row.id}/ops-review`, { approve: true });
      toast.success(`${agencies[row.agency_id] || "Rezervasyon"} onaylandı — fiyat sabitlendi`);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      loadSummary();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Onaylanamadı");
    }
  };

  const openOverride = (row) => {
    // Iter 28: Fiyat Değiştir artık birim fiyat düzenler — acente ile aynı
    // hesaplama dilini konuşuruz. Boş bırakırsa acentenin gönderdiği birim
    // fiyatı seed olarak koyar.
    setOverrideTarget({
      row,
      roomUnit: Number(row.room_unit_price || 0),
      mealUnit: Number(row.meal_unit_price || 0),
      note: "",
      busy: false,
    });
  };

  const submitOverride = async () => {
    if (!overrideTarget) return;
    const { row, roomUnit, mealUnit, note } = overrideTarget;
    // Aynı formülü acente formunda kullanıyoruz — burada yeniden hesap ederiz
    // ki backend'e gönderdiğimiz toplamlar UI'daki büyük "Yeni Toplam" ile
    // birebir tutsun.
    const { roomTotal, mealTotal } = computeReservationTotal({
      rooms: row.rooms,
      nights: row.nights,
      adults: row.adults,
      children: row.children,
      staff: row.staff,
      meal_start: row.meal_start,
      meal_end: row.meal_end,
      room_unit_price: Math.max(0, Number(roomUnit) || 0),
      meal_unit_price: Math.max(0, Number(mealUnit) || 0),
    });
    setOverrideTarget({ ...overrideTarget, busy: true });
    try {
      await panelApi.post(`/reservations/${row.id}/ops-review`, {
        approve: true,
        // Backend accepts both TOPLAM (room_price/meal_price) — used for
        // legacy history and Excel exports — and the raw unit numbers so
        // reopening the reservation form shows the ops-approved birim.
        room_price: roomTotal,
        meal_price: mealTotal,
        room_unit_price: Math.max(0, Number(roomUnit) || 0),
        meal_unit_price: Math.max(0, Number(mealUnit) || 0),
        ops_note: note
          ? `${note} · Birim güncellendi: Oda ${roomUnit} ﷼/gece, Yemek ${mealUnit} ﷼/gün`
          : `Birim güncellendi: Oda ${roomUnit} ﷼/gece, Yemek ${mealUnit} ﷼/gün`,
      });
      toast.success("Yeni fiyat kaydedildi — rezervasyon onaylandı");
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setOverrideTarget(null);
      loadSummary();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kaydedilemedi");
      setOverrideTarget((s) => (s ? { ...s, busy: false } : s));
    }
  };

  const openReject = (row) => setRejectTarget({ row, reason: "", busy: false });

  const submitReject = async () => {
    if (!rejectTarget) return;
    const reason = (rejectTarget.reason || "").trim();
    if (!reason) {
      toast.error("Reddetme sebebi zorunludur");
      return;
    }
    setRejectTarget({ ...rejectTarget, busy: true });
    try {
      await panelApi.post(`/reservations/${rejectTarget.row.id}/ops-review`, {
        approve: false,
        ops_note: reason,
      });
      toast.success("Rezervasyon reddedildi — acenteye sebep iletilecek");
      setRows((prev) => prev.filter((r) => r.id !== rejectTarget.row.id));
      setRejectTarget(null);
      loadSummary();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reddedilemedi");
      setRejectTarget((s) => (s ? { ...s, busy: false } : s));
    }
  };

  // Iter 29: toplu seçim yardımcıları.
  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const filteredIds = filtered.map((r) => r.id);
  const allChecked = filteredIds.length > 0 && filteredIds.every((id) => selectedIds.has(id));
  const someChecked = filteredIds.some((id) => selectedIds.has(id)) && !allChecked;
  const toggleAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allChecked) {
        filteredIds.forEach((id) => next.delete(id));
      } else {
        filteredIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectedCount = filteredIds.filter((id) => selectedIds.has(id)).length;

  const bulkApprove = async () => {
    const ids = filteredIds.filter((id) => selectedIds.has(id));
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const r = await panelApi.post("/reservations/bulk-approve", { ids });
      const n = r?.data?.approved || 0;
      toast.success(`${n} rezervasyon onaylandı`);
      setRows((prev) => prev.filter((row) => !ids.includes(row.id)));
      clearSelection();
      loadSummary();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Toplu onay yapılamadı");
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkReject = async () => {
    const ids = filteredIds.filter((id) => selectedIds.has(id));
    if (!ids.length) return;
    const reason = (bulkRejectReason || "").trim();
    if (!reason) {
      toast.error("Reddetme sebebi zorunludur");
      return;
    }
    setBulkBusy(true);
    try {
      const r = await panelApi.post("/reservations/bulk-reject", { ids, reason });
      const n = r?.data?.rejected || 0;
      toast.success(`${n} rezervasyon reddedildi — acenteye sebep iletilecek`);
      setRows((prev) => prev.filter((row) => !ids.includes(row.id)));
      clearSelection();
      setBulkRejectOpen(false);
      setBulkRejectReason("");
      loadSummary();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Toplu reddet yapılamadı");
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="acente-ops-pricing-page">
      <div>
        <Link to="/acente/rezervasyonlar" className="text-sm text-brand-navy/60 hover:text-brand-orange inline-flex items-center gap-1">
          <ArrowLeft size={14} /> Rezervasyonlara Dön
        </Link>
        <div className="mt-2 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-blue-700 font-semibold">Operasyon · Fiyat İncelemesi</div>
            <h1 className="font-serif text-3xl text-brand-navy mt-1">Fiyat Onayları</h1>
            <p className="text-xs text-slate-500 mt-1">
              Acentelerin size ilettiği fiyat bilgilerini tek tıkla onaylayın ya da yeni fiyat girin.
            </p>
          </div>
        </div>

        {/* Iter 30: Sabah özeti KPI stripi — ops paneline girdiğinde günün
            önceliklerini bir satırda görsün. Her kart:
              - Bekleyen: en önemli sayı (turuncu, büyük)
              - Bugün Girişleri / Yarın Girişleri: yaklaşan katering günleri
              - Bu Hafta Onaylanan: hız ölçümü (motivational metric)
              - Reddedilen: acentelerin henüz revize etmediği kayıtlar
            Kart kendi başına tıklanabilir → arama kutusu ilgili filtreye
            geçmesi için yönlendirmez; sadece bilgi kartı. */}
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="ops-morning-summary">
          <KPICard
            icon={<Wallet size={16} />}
            label="Bekleyen"
            value={summary?.pending ?? "…"}
            tone="orange"
            testid="ops-kpi-pending"
          />
          <KPICard
            icon={<Sunrise size={16} />}
            label="Bugün Girişleri"
            value={summary?.checkin_today ?? "…"}
            tone={summary?.checkin_today > 0 ? "rose" : "slate"}
            testid="ops-kpi-checkin-today"
          />
          <KPICard
            icon={<Clock size={16} />}
            label="Yarın Girişleri"
            value={summary?.checkin_tomorrow ?? "…"}
            tone={summary?.checkin_tomorrow > 0 ? "amber" : "slate"}
            testid="ops-kpi-checkin-tomorrow"
          />
          <KPICard
            icon={<ThumbsUp size={16} />}
            label="Bu Hafta Onaylanan"
            value={summary?.approved_week ?? "…"}
            tone="emerald"
            testid="ops-kpi-approved-week"
          />
          <KPICard
            icon={<AlertCircle size={16} />}
            label="Reddedilen"
            hint="acente revize etmedi"
            value={summary?.rejected_active ?? "…"}
            tone={summary?.rejected_active > 0 ? "rose" : "slate"}
            testid="ops-kpi-rejected"
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-brand-navy/10 shadow-sm">
        <div className="p-4 border-b border-slate-100 flex items-center gap-3">
          <Input
            placeholder="Şirket / Numara / Grup / Otel ara..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="max-w-md"
            data-testid="ops-pricing-search"
          />
          <button
            onClick={load}
            className="ml-auto text-xs px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 inline-flex items-center gap-1"
            title="Listeyi yenile"
            data-testid="ops-pricing-refresh"
          >
            <RefreshCw size={12} /> Yenile
          </button>
        </div>

        {/* Iter 29: toplu seçim aksiyon barı — yalnızca 1+ satır seçildiğinde
            görünür. Üstte sabit değil, akışta kayıyor; ekranı sadeleştirir. */}
        {selectedCount > 0 && (
          <div
            className="rounded-2xl bg-brand-orange/5 border border-brand-orange/30 px-4 py-2.5 flex items-center gap-3 flex-wrap"
            data-testid="ops-pricing-bulk-bar"
          >
            <div className="text-sm text-brand-navy font-medium">
              <span className="inline-flex items-center justify-center min-w-[24px] h-[24px] px-2 rounded-full bg-brand-orange text-white text-xs font-bold mr-2" data-testid="ops-pricing-bulk-count">
                {selectedCount}
              </span>
              rezervasyon seçildi
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Button
                size="sm"
                onClick={bulkApprove}
                disabled={bulkBusy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="ops-pricing-bulk-approve"
              >
                <Check size={14} className="mr-1" /> Toplu Onayla
              </Button>
              <Button
                size="sm"
                onClick={() => setBulkRejectOpen(true)}
                disabled={bulkBusy}
                variant="outline"
                className="border-rose-300 text-rose-700 hover:bg-rose-50"
                data-testid="ops-pricing-bulk-reject"
              >
                <X size={14} className="mr-1" /> Toplu Reddet
              </Button>
              <button
                onClick={clearSelection}
                className="text-xs text-slate-500 hover:text-brand-orange px-2"
                data-testid="ops-pricing-bulk-clear"
              >
                Temizle
              </button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ops-pricing-table">
            <thead className="text-xs uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
              <tr className="text-left">
                <th className="py-2 px-2 w-8">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = someChecked; }}
                    onChange={toggleAll}
                    className="rounded border-slate-300 cursor-pointer accent-brand-orange"
                    aria-label="Tüm görünen satırları seç/kaldır"
                    data-testid="ops-pricing-select-all"
                  />
                </th>
                <th className="py-2 px-3">Şirket / Acente</th>
                <th className="py-2 px-2">Giriş Tarihi</th>
                <th className="py-2 px-2 text-right">Oda Birim <span className="text-[10px] normal-case text-slate-400">(oda / gece)</span></th>
                <th className="py-2 px-2 text-right">Yemek Birim <span className="text-[10px] normal-case text-slate-400">(kişi / gün · 2 öğün)</span></th>
                <th className="py-2 px-2 text-right">Toplam (SAR)</th>
                <th className="py-2 px-2 w-64 text-right">Onaylıyor musun?</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="py-6 text-center text-slate-500">Yükleniyor...</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-slate-500">
                    <div className="inline-flex flex-col items-center gap-2">
                      <div className="w-14 h-14 rounded-full bg-emerald-50 text-emerald-500 flex items-center justify-center">
                        <CheckCircle2 size={22} />
                      </div>
                      <div className="text-sm">İncelenecek fiyat yok.</div>
                      <div className="text-xs text-slate-400">Acenteler yeni rezervasyon iletince burada listelenir.</div>
                    </div>
                  </td>
                </tr>
              )}
              {filtered.map((r) => {
                const total = Number(r.agreed_room_price || 0) + Number(r.agreed_meal_price || 0);
                // Iter 26 breakdown: acentenin girdiği birim fiyatlar
                // varsa ekranda göster ki operasyon "300 × 2 oda × 5 gece = 3000"
                // gibi hesabı anında görebilsin.
                const rooms = totalRoomsFromDist(r.rooms);
                const nights = Number(r.nights || 0);
                const adults = Number(r.adults || 0);
                const meals = totalMealCount({
                  meal_start: r.meal_start,
                  meal_end: r.meal_end,
                  nights,
                });
                const roomUnit = Number(r.room_unit_price || 0);
                const mealUnit = Number(r.meal_unit_price || 0);
                const perMeal = mealUnit / 2;
                return (
                  <tr
                    key={r.id}
                    className={`border-b border-slate-100 transition-colors ${
                      selectedIds.has(r.id) ? "bg-brand-orange/5 hover:bg-brand-orange/10" : "hover:bg-blue-50/40"
                    }`}
                    data-testid={`ops-pricing-row-${r.id}`}
                  >
                    <td className="py-3 px-2 align-top">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(r.id)}
                        onChange={() => toggleRow(r.id)}
                        className="rounded border-slate-300 cursor-pointer accent-brand-orange"
                        aria-label="Bu rezervasyonu seç"
                        data-testid={`ops-pricing-select-${r.id}`}
                      />
                    </td>
                    <td className="py-3 px-3 align-top">
                      <div className="flex items-start gap-2">
                        <Building2 size={14} className="text-blue-600 mt-0.5" />
                        <div>
                          <div className="font-medium text-brand-navy flex items-center gap-2 flex-wrap" data-testid={`ops-pricing-agency-${r.id}`}>
                            {agencies[r.agency_id] || <span className="text-slate-400 italic">Bağımsız</span>}
                            {r._risk?.isNewAgency && (
                              <span
                                className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-widest bg-purple-100 text-purple-700 border border-purple-200"
                                title="Bu acenteden ilk rezervasyonlardan biri"
                                data-testid={`ops-pricing-new-agency-${r.id}`}
                              >
                                Yeni acente
                              </span>
                            )}
                            {r._risk?.daysUntil <= 7 && r._risk?.daysUntil >= 0 && (
                              <span
                                className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-widest bg-orange-100 text-orange-700 border border-orange-200"
                                title="Girişe az kaldı — öncelikli"
                                data-testid={`ops-pricing-imminent-${r.id}`}
                              >
                                {r._risk.daysUntil === 0 ? "Bugün" : `${r._risk.daysUntil}g kaldı`}
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500 font-mono mt-0.5">{r.reservation_number}</div>
                          {r.group_name && <div className="text-[11px] text-slate-500 mt-0.5">{r.group_name}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-2 align-top text-sm">
                      <div className="inline-flex items-center gap-1 text-brand-navy">
                        <Calendar size={12} className="text-brand-orange" />
                        <span data-testid={`ops-pricing-date-${r.id}`}>{formatTr(r.checkin)}</span>
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{nights} gece</div>
                      <div className="text-[11px] text-brand-navy mt-0.5 font-medium" data-testid={`ops-pricing-rooms-${r.id}`}>
                        {rooms} oda
                      </div>
                    </td>
                    <td className="py-3 px-2 text-right align-top text-brand-navy" data-testid={`ops-pricing-room-${r.id}`}>
                      {/* Iter 27b: birim fiyat en üstte, büyük — ops'un
                          hızlıca "oda başına makul mü?" kontrol edebilmesi
                          için görsel hiyerarşi ters çevrildi. Toplam alta
                          küçük yazıyla düştü. */}
                      {roomUnit > 0 ? (
                        <>
                          <div className="font-serif text-2xl font-semibold text-brand-navy" data-testid={`ops-pricing-room-unit-${r.id}`}>
                            {roomUnit.toLocaleString("tr-TR")}
                            <span className="ml-0.5 text-xs text-brand-orange">﷼</span>
                          </div>
                          <div className="text-[10px] text-slate-500 mt-0.5">
                            {rooms} oda × {nights} gece
                          </div>
                          <div className="text-[11px] text-slate-600 font-medium mt-0.5" data-testid={`ops-pricing-room-total-${r.id}`}>
                            Toplam: {Number(r.agreed_room_price || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 })} ﷼
                          </div>
                        </>
                      ) : (
                        <div className="text-sm font-semibold">
                          {Number(r.agreed_room_price || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 })}
                          <span className="ml-0.5 text-xs text-brand-orange">﷼</span>
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-2 text-right align-top text-brand-navy" data-testid={`ops-pricing-meal-${r.id}`}>
                      {mealUnit > 0 ? (
                        <>
                          <div className="font-serif text-2xl font-semibold text-brand-navy" data-testid={`ops-pricing-meal-unit-${r.id}`}>
                            {mealUnit.toLocaleString("tr-TR")}
                            <span className="ml-0.5 text-xs text-brand-orange">﷼</span>
                          </div>
                          <div className="text-[10px] text-slate-500 mt-0.5">
                            {adults} yetişkin × {nights + 1} gün
                          </div>
                          <div className="text-[11px] text-slate-600 font-medium mt-0.5" data-testid={`ops-pricing-meal-total-${r.id}`}>
                            Toplam: {Number(r.agreed_meal_price || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 })} ﷼
                          </div>
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            (öğün başı {perMeal.toLocaleString("tr-TR", { maximumFractionDigits: 2 })} ﷼ · {meals} öğün)
                          </div>
                        </>
                      ) : (
                        <div className="text-sm font-semibold">
                          {Number(r.agreed_meal_price || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 })}
                          <span className="ml-0.5 text-xs text-brand-orange">﷼</span>
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-2 text-right align-top font-serif text-lg text-brand-orange-dark" data-testid={`ops-pricing-total-${r.id}`}>
                      {total.toLocaleString("tr-TR", { maximumFractionDigits: 2 })}
                      <span className="ml-0.5 text-xs text-brand-orange">﷼</span>
                    </td>
                    <td className="py-3 px-2 text-right align-top">
                      <div className="inline-flex gap-1 flex-wrap justify-end">
                        <button
                          onClick={() => approveAsIs(r)}
                          className="text-xs px-3 py-1.5 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 inline-flex items-center gap-1"
                          data-testid={`ops-pricing-approve-${r.id}`}
                          title="Fiyatı olduğu gibi onayla ve sabitle"
                        >
                          <CheckCircle2 size={12} /> Onayla
                        </button>
                        <button
                          onClick={() => openOverride(r)}
                          className="text-xs px-3 py-1.5 rounded-full bg-brand-orange text-white hover:bg-brand-orange-dark inline-flex items-center gap-1"
                          data-testid={`ops-pricing-override-${r.id}`}
                          title="Yeni fiyat gir"
                        >
                          <Pencil size={12} /> Fiyat Değiştir
                        </button>
                        <button
                          onClick={() => openReject(r)}
                          className="text-xs px-3 py-1.5 rounded-full bg-rose-500 text-white hover:bg-rose-600 inline-flex items-center gap-1"
                          data-testid={`ops-pricing-reject-${r.id}`}
                          title="Reddet ve acenteye sebep gönder"
                        >
                          <XCircle size={12} /> Reddet
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <AlertDialog open={!!overrideTarget} onOpenChange={(v) => !v && setOverrideTarget(null)}>
        <AlertDialogContent data-testid="ops-pricing-override-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Yeni Fiyat Gir</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{overrideTarget?.row ? (agencies[overrideTarget.row.agency_id] || overrideTarget.row.reservation_number) : ""}</b>
              {" "}için birim fiyatları düzenleyin. Toplam otomatik hesaplanır; rezervasyon onaylanır ve kilitlenir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {overrideTarget && (() => {
            const row = overrideTarget.row;
            const rooms = totalRoomsFromDist(row.rooms);
            const nights = Number(row.nights || 0);
            const adults = Number(row.adults || 0);
            const meals = totalMealCount({ meal_start: row.meal_start, meal_end: row.meal_end, nights });
            const acRoomUnit = Number(row.room_unit_price || 0);
            const acMealUnit = Number(row.meal_unit_price || 0);
            const newRoomUnit = Math.max(0, Number(overrideTarget.roomUnit) || 0);
            const newMealUnit = Math.max(0, Number(overrideTarget.mealUnit) || 0);
            const newPerMeal = newMealUnit / 2;
            const newRoomTotal = newRoomUnit * rooms * nights;
            const newMealTotal = newPerMeal * meals * adults;
            const newGrand = newRoomTotal + newMealTotal;
            const acGrand = Number(row.agreed_room_price || 0) + Number(row.agreed_meal_price || 0);
            const delta = newGrand - acGrand;
            return (
              <div className="grid gap-3">
                {/* Acente'nin girdiği birim + toplam — reference karesi */}
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs">
                  <div className="text-slate-500 uppercase tracking-widest text-[10px] font-semibold">Acentenin Girdiği</div>
                  <div className="mt-1 grid grid-cols-3 gap-3 text-brand-navy">
                    <div>
                      <div className="text-[9px] uppercase text-slate-400">Oda / gece</div>
                      <div className="text-base font-semibold">{acRoomUnit.toLocaleString("tr-TR")} <span className="text-[10px] text-slate-500">﷼</span></div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase text-slate-400">Yemek / gün</div>
                      <div className="text-base font-semibold">{acMealUnit.toLocaleString("tr-TR")} <span className="text-[10px] text-slate-500">﷼</span></div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase text-slate-400">Toplam</div>
                      <div className="text-base font-semibold">{acGrand.toLocaleString("tr-TR", { maximumFractionDigits: 2 })} <span className="text-[10px] text-slate-500">﷼</span></div>
                    </div>
                  </div>
                  <div className="mt-2 pt-2 border-t border-slate-200 text-[10px] text-slate-500 flex items-center gap-3">
                    <span><b>{rooms}</b> oda</span>
                    <span><b>{nights}</b> gece</span>
                    <span><b>{adults}</b> yetişkin</span>
                    <span><b>{meals}</b> öğün</span>
                  </div>
                </div>

                {/* Ops'un girdiği yeni birim fiyatlar */}
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label className="text-brand-orange-dark">Yeni Oda Fiyatı <span className="text-[10px] text-slate-500">(1 oda / 1 gece)</span></Label>
                    <div className="relative">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={overrideTarget.roomUnit}
                        onChange={(e) => setOverrideTarget((s) => ({ ...s, roomUnit: e.target.value }))}
                        data-testid="ops-pricing-override-room"
                        className="pr-8"
                        autoFocus
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">﷼</span>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1" data-testid="ops-pricing-override-room-total">
                      = {newRoomTotal.toLocaleString("tr-TR", { maximumFractionDigits: 2 })} ﷼ <span className="text-slate-400">({rooms} × {nights})</span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-brand-orange-dark">Yeni Yemek Fiyatı <span className="text-[10px] text-slate-500">(1 kişi / 1 gün · 2 öğün)</span></Label>
                    <div className="relative">
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={overrideTarget.mealUnit}
                        onChange={(e) => setOverrideTarget((s) => ({ ...s, mealUnit: e.target.value }))}
                        data-testid="ops-pricing-override-meal"
                        className="pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">﷼</span>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1" data-testid="ops-pricing-override-meal-total">
                      = {newMealTotal.toLocaleString("tr-TR", { maximumFractionDigits: 2 })} ﷼ <span className="text-slate-400">(öğün başı {newPerMeal.toLocaleString("tr-TR", { maximumFractionDigits: 2 })} × {meals} × {adults})</span>
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Not (opsiyonel)</Label>
                  <Input
                    value={overrideTarget.note}
                    onChange={(e) => setOverrideTarget((s) => ({ ...s, note: e.target.value }))}
                    placeholder="Acenteye/log'a düşecek kısa açıklama"
                    data-testid="ops-pricing-override-note"
                  />
                </div>

                {/* Yeni toplam + değişim delta */}
                <div className="rounded-xl bg-brand-orange/10 border border-brand-orange/30 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <span className="text-brand-orange-dark uppercase tracking-widest text-xs font-semibold">Yeni Toplam</span>
                    <span className="font-serif text-2xl text-brand-orange-dark" data-testid="ops-pricing-override-total">
                      {newGrand.toLocaleString("tr-TR", { maximumFractionDigits: 2 })}{" "}
                      <span className="text-slate-500 text-sm">SAR ﷼</span>
                    </span>
                  </div>
                  {delta !== 0 && (
                    <div className={`mt-1.5 text-[11px] flex items-center justify-end gap-1 font-medium ${delta > 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid="ops-pricing-override-delta">
                      Acenteye göre {delta > 0 ? "+" : ""}{delta.toLocaleString("tr-TR", { maximumFractionDigits: 2 })} ﷼
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ops-pricing-override-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={submitOverride}
              disabled={!!overrideTarget?.busy}
              className="bg-brand-orange hover:bg-brand-orange-dark focus:ring-brand-orange"
              data-testid="ops-pricing-override-confirm"
            >
              {overrideTarget?.busy ? "Kaydediliyor..." : "Fiyatı Kaydet ve Onayla"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!rejectTarget} onOpenChange={(v) => !v && setRejectTarget(null)}>
        <AlertDialogContent data-testid="ops-pricing-reject-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Rezervasyonu Reddet</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{rejectTarget?.row ? (agencies[rejectTarget.row.agency_id] || rejectTarget.row.reservation_number) : ""}</b>
              {" "}rezervasyonu reddedilecek. Sebep acenteye kırmızı bir uyarı olarak gösterilir; acente düzeltip tekrar iletebilir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {rejectTarget && (
            <div className="grid gap-3">
              <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-xs text-rose-900">
                <div className="text-rose-700 uppercase tracking-widest">Acentenin girdiği</div>
                <div className="mt-1">
                  Oda: <b>{Number(rejectTarget.row.agreed_room_price || 0).toLocaleString("tr-TR")}</b>{" · "}
                  Yemek: <b>{Number(rejectTarget.row.agreed_meal_price || 0).toLocaleString("tr-TR")}</b>{" · "}
                  Toplam: <b>{(Number(rejectTarget.row.agreed_room_price || 0) + Number(rejectTarget.row.agreed_meal_price || 0)).toLocaleString("tr-TR")} SAR</b>
                </div>
              </div>
              <div>
                <Label>Reddetme Sebebi <span className="text-rose-600">*</span></Label>
                <Input
                  value={rejectTarget.reason}
                  onChange={(e) => setRejectTarget((s) => ({ ...s, reason: e.target.value }))}
                  placeholder="Örn. Oda fiyatı çok düşük, yemek yer almamış, tarih uyuşmazlığı..."
                  data-testid="ops-pricing-reject-reason"
                  autoFocus
                />
                <p className="text-[11px] text-slate-500 mt-1">Acente bu mesajı görecek — kısa ve açıklayıcı yazın.</p>
              </div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="ops-pricing-reject-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={submitReject}
              disabled={!!rejectTarget?.busy || !(rejectTarget?.reason || "").trim()}
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
              data-testid="ops-pricing-reject-confirm"
            >
              {rejectTarget?.busy ? "Reddediliyor..." : "Reddet ve İlet"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Iter 29: Toplu reddet — tek bir sebep tüm seçili satırlara yazılır. */}
      <AlertDialog open={bulkRejectOpen} onOpenChange={(v) => !v && setBulkRejectOpen(false)}>
        <AlertDialogContent data-testid="ops-pricing-bulk-reject-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Toplu Reddet</AlertDialogTitle>
            <AlertDialogDescription>
              Seçili <b>{selectedCount}</b> rezervasyon reddedilecek. Aynı sebep tüm satırlara yazılır ve acentelere iletilir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid gap-2">
            <Label>Reddetme Sebebi *</Label>
            <Input
              value={bulkRejectReason}
              onChange={(e) => setBulkRejectReason(e.target.value)}
              placeholder="Örn: Otel için piyasa fiyatı çok yüksek, revize edip yeniden gönderin"
              data-testid="ops-pricing-bulk-reject-reason"
              autoFocus
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="ops-pricing-bulk-reject-cancel"
              onClick={() => { setBulkRejectReason(""); setBulkRejectOpen(false); }}
            >
              Vazgeç
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={bulkReject}
              disabled={bulkBusy || !bulkRejectReason.trim()}
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
              data-testid="ops-pricing-bulk-reject-confirm"
            >
              {bulkBusy ? "Reddediliyor..." : `${selectedCount} Rezervasyonu Reddet`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
